import { Trans, useLingui } from '@lingui/react/macro'
import {
  ActionIcon,
  Box,
  Card,
  Group,
  Menu,
  Stack,
  Table,
  Text,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import type { Consumption } from '@markku/types'
import { formatDate, formatEuros } from '@markku/utils'
import { IconDotsVertical, IconEdit, IconTrash } from '@tabler/icons-react'
import { TYPE_CONFIG } from './utils'

type ConsumptionTableProps = {
  consumptions: Consumption[]
  onEdit: (consumption: Consumption) => void
  onDelete: (id: number) => void
}

export function ConsumptionTable({
  consumptions,
  onEdit,
  onDelete,
}: ConsumptionTableProps) {
  const { t } = useLingui()
  const handleDelete = (consumption: Consumption) => {
    modals.openConfirmModal({
      title: t`Delete consumption`,
      centered: true,
      children: (
        <Text>
          <Trans>
            Are you sure you want to delete this meter reading (
            {Number(consumption.meterReading).toFixed(1)} {consumption.unit},{' '}
            {formatDate(consumption.date)})?
          </Trans>
        </Text>
      ),
      labels: { confirm: t`Delete`, cancel: t`Cancel` },
      confirmProps: { color: 'red' },
      onConfirm: () => onDelete(consumption.id),
    })
  }

  if (consumptions.length === 0) {
    return null
  }

  return (
    <Card withBorder radius="lg" p="md">
      <Stack gap="md">
        <Text fw={600} size="sm">
          <Trans>Meter readings</Trans>
        </Text>

        <Table.ScrollContainer minWidth={500}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>
                  <Trans>Date</Trans>
                </Table.Th>
                <Table.Th>
                  <Trans>Type</Trans>
                </Table.Th>
                <Table.Th>
                  <Trans>Meter reading</Trans>
                </Table.Th>
                <Table.Th>
                  <Trans>Cost</Trans>
                </Table.Th>
                <Table.Th w={50}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {consumptions.map((consumption) => {
                const config = TYPE_CONFIG[consumption.type]
                const meterReading = consumption.meterReading
                  ? Number(consumption.meterReading).toFixed(1)
                  : '-'
                return (
                  <Table.Tr key={consumption.id}>
                    <Table.Td>
                      <Text size="sm">{formatDate(consumption.date)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Box
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: 4,
                            background: `var(--mantine-color-${config.color})`,
                          }}
                        />
                        <Text size="sm">{t(config.label)}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {meterReading} {consumption.unit}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text
                        size="sm"
                        c={consumption.cost ? undefined : 'dimmed'}
                      >
                        {consumption.cost
                          ? formatEuros(parseFloat(consumption.cost))
                          : '-'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Menu position="bottom-end" withinPortal>
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray">
                            <IconDotsVertical size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            leftSection={<IconEdit size={16} />}
                            onClick={() => onEdit(consumption)}
                          >
                            <Trans>Edit</Trans>
                          </Menu.Item>
                          <Menu.Item
                            leftSection={<IconTrash size={16} />}
                            color="red"
                            onClick={() => handleDelete(consumption)}
                          >
                            <Trans>Delete</Trans>
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Stack>
    </Card>
  )
}
